import AccountContext from 'context/AccountContext';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Maybe, PaymentType } from 'types/generatedSchemaTypes';
import {
  getPaymentMethodLogo,
  getWalletPaymentMethodIcon,
  getWalletPaymentMethodLabelKey,
  isWalletCard,
} from 'utils/paymentUtils.ts';

export type PaymentDetails = {
  paymentMethodTitle?: string;
  paymentMethodName?: string;
  paymentMethodLogoName?: string;
  paymentType?: PaymentType;
};

type UsePaymentDetailsProps = {
  transactionId?: Maybe<string>;
  cardId?: Maybe<string>;
  totalAmount?: Maybe<number>;
  corporateAccountUid?: Maybe<string>;
  otherPaymentProvider?: Maybe<string>;
  paymentType?: Maybe<PaymentType>;
};

const usePaymentDetails = ({
  transactionId: paymentTransactionId,
  cardId,
  totalAmount,
  corporateAccountUid,
  otherPaymentProvider,
  paymentType,
}: UsePaymentDetailsProps): PaymentDetails | undefined => {
  const {
    customer: { paymentCards, companyBenefits },
  } = useContext(AccountContext);
  const { t } = useTranslation();

  if (!paymentTransactionId) {
    if (totalAmount === null) {
      return {
        paymentMethodName: '',
      };
    }
    if (totalAmount === 0) {
      return {
        paymentMethodName: t('paymentMethods.freeParking'),
      };
    }
  }
  // Helper: extract credit card details
  const getCreditCardDetails = (): PaymentDetails => {
    const paymentCard = paymentCards?.find((card) => card?.cardId === cardId);

    // A stored wallet card shows its custom name (wallet label fallback) and
    // its wallet brand mark — never the underlying scheme's PAN or logo.
    // Mirrors the CardItem row / selector treatment.
    if (isWalletCard(paymentCard)) {
      const walletLabelKey = getWalletPaymentMethodLabelKey(
        paymentCard?.paymentMethod
      );
      return {
        paymentMethodTitle: t('services.paymentMethod'),
        paymentMethodName:
          paymentCard?.description || (walletLabelKey ? t(walletLabelKey) : ''),
        paymentMethodLogoName: getWalletPaymentMethodIcon(
          paymentCard?.paymentMethod
        ),
        paymentType: 'CREDIT_CARD',
      };
    }

    return {
      paymentMethodTitle: t('payment.creditCardOnly'),
      paymentMethodName: paymentCard?.lastNums
        ? `**** ${paymentCard.lastNums}`
        : t('payment.creditCardMissing'),
      paymentMethodLogoName: getPaymentMethodLogo(
        'CREDIT_CARD',
        paymentCard?.type
      )?.logoName,
      paymentType: 'CREDIT_CARD',
    };
  };

  // Helper: extract corporate account details
  const getCorporateAccountDetails = (): PaymentDetails => {
    const companyBenefit = companyBenefits?.find(
      (benefit) => benefit?.benefitTargetUid === corporateAccountUid
    );
    return {
      paymentMethodTitle: t('account.companyPermit'),
      paymentMethodName: companyBenefit?.name,
      paymentMethodLogoName:
        getPaymentMethodLogo('CORPORATE_ACCOUNT')?.logoName,
      paymentType: 'CORPORATE_ACCOUNT',
    };
  };
  const getMobilePayDetails = (): PaymentDetails => {
    return {
      paymentMethodTitle: t('paymentMethods.mobilePay'),
      paymentMethodName: t('paymentMethods.mobilePay'),
      paymentMethodLogoName: getPaymentMethodLogo('MOBILEPAY')?.logoName,
      paymentType: 'MOBILEPAY',
    };
  };

  if (paymentType === 'CREDIT_CARD') {
    return getCreditCardDetails();
  }

  if (paymentType === 'CORPORATE_ACCOUNT') {
    return getCorporateAccountDetails();
  }

  if (paymentType === 'MOBILEPAY') {
    return getMobilePayDetails();
  }

  if (paymentType === 'SWISH') {
    return {
      paymentMethodTitle: t('paymentMethods.swish'),
      paymentMethodName: t('paymentMethods.swish'),
      paymentMethodLogoName: getPaymentMethodLogo('SWISH')?.logoName,
      paymentType: 'SWISH',
    };
  }

  if (paymentType === 'PAY_AFTERWARDS') {
    return {
      paymentMethodTitle: t('paymentMethods.otherPaymentMethod'),
      paymentMethodName: t('paymentMethods.payAfterwards'),
      paymentType: 'PAY_AFTERWARDS',
    };
  }

  if (otherPaymentProvider) {
    return {
      paymentMethodTitle: otherPaymentProvider,
      paymentMethodName: otherPaymentProvider,
      paymentType: 'EXTERNAL',
    };
  }

  return undefined;
};

export default usePaymentDetails;
